import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Spin, Button } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useToken from "../Authentication/useToken";
import Constants from "../Utils/Constants";

import PatientStats from "./PatientStats";
import {
  parseStoredRoles,
  filterVisiblePatients,
  selectPatientFromNavigation,
  getStatsErrorDescription,
  getEmptyPatientsMessage,
} from "./PatientStatsHub.helpers";
import "./Styles.css";

export default function PatientStatsHub() {
  const { t } = useTranslation("Patients");
  const { token } = useToken();
  const location = useLocation();

  const [programType, setProgramType] = useState("PACE"); // "PACE" | "MATCH" | "PATH"
  const roles = useMemo(() => parseStoredRoles(), []);
  const isDoctorRole = roles.includes("doctor");
  const isAdminRole = roles.some((role) =>
    ["admin", "superadmin", "superuser"].includes(role)
  );

  // Handler injected by PatientStats to trigger CSV export from header button.
  const [exportCsvHandler, setExportCsvHandler] = useState(null);
  const handleExportCsvReady = useCallback((handler) => {
    setExportCsvHandler((prev) => (prev === handler ? prev : handler));
  }, []);

  const fetchConfig = useMemo(
    () => ({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
    [token]
  );

  const {
    data: patients = [],
    isLoading,
    isError,
  } = useQuery(
    ["Patients", programType, token, isDoctorRole],
    async () => {
      const endpoint = isDoctorRole
        ? `${Constants.SERVER_URL}/patients`
        : `${Constants.SERVER_URL}/stats/patients?type=${programType}`;
      const res = await axios.get(endpoint, fetchConfig);
      return Array.isArray(res.data) ? res.data : [];
    },
    {
      enabled: !!token,
      staleTime: 60_000,
    }
  );

  const {
    data: doctorEligibilityRequests = [],
    isLoading: isDoctorEligibilityLoading,
    isError: isDoctorEligibilityError,
  } = useQuery(
    ["doctorEligibilityRequestsForStats", token, isDoctorRole],
    async () => {
      const endpoint = `${Constants.SERVER_URL}/eligibility-request?view=doctor`;
      const res = await axios.get(endpoint, fetchConfig);
      return Array.isArray(res.data) ? res.data : [];
    },
    {
      enabled: !!token && isDoctorRole,
      staleTime: 60_000,
    }
  );

  const visiblePatients = useMemo(() => {
    return filterVisiblePatients(
      patients,
      isDoctorRole,
      doctorEligibilityRequests
    );
  }, [patients, isDoctorRole, doctorEligibilityRequests]);

  const isPageLoading = isLoading || (isDoctorRole && isDoctorEligibilityLoading);
  const isPageError = isError || (isDoctorRole && isDoctorEligibilityError);

  const selectedPatientFromNavigation = useMemo(() => {
    return selectPatientFromNavigation(visiblePatients, location.state);
  }, [visiblePatients, location.state]);

  if (!token) {
    return (
      <div className="patient-data-container">
        <Alert
          type="warning"
          showIcon
          message={t("stats_missing_token", "Token absent : reconnecte-toi.")}
        />
      </div>
    );
  }

  return (
    <div className="patient-data-container">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "0 0 16px 0",
        }}
      >
        <h1 style={{ margin: 0 }}>{t("stats_title", "Statistiques")}</h1>

        {isAdminRole ? (
          <Button
            type="primary"
            onClick={() => {
              if (typeof exportCsvHandler === "function") exportCsvHandler();
            }}
            disabled={
              isPageLoading ||
              isPageError ||
              visiblePatients.length === 0 ||
              typeof exportCsvHandler !== "function"
            }
          >
            {t("button_export_to_csv", "Exporter en CSV")}
          </Button>
        ) : null}
      </div>

      {isPageLoading ? (
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spin />
        </div>
      ) : isPageError ? (
        <Alert
          type="error"
          showIcon
          message={t("stats_error_loading_patients", "Impossible de charger la liste des patients")}
          description={getStatsErrorDescription(isDoctorRole, t)}
        />
      ) : visiblePatients.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message={getEmptyPatientsMessage(isDoctorRole, programType, t)}
        />
      ) : (
        <PatientStats
          patients={visiblePatients}
          patient={selectedPatientFromNavigation ?? visiblePatients[0]}
          programType={programType}
          setProgramType={setProgramType}
          onExportCsvReady={handleExportCsvReady}
        />
      )}
    </div>
  );
}
